/**
 * Cartridges Settings Page
 *
 * Admin UI for managing cartridges (modular features/integrations)
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from '../../hooks/useTranslation'
import {
  PuzzlePieceIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  StopIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  ChevronRightIcon,
  XMarkIcon,
  HeartIcon,
  LinkIcon,
  DocumentTextIcon,
  CubeIcon,
  CloudIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/20/solid'

// ===========================================
// Types
// ===========================================

interface CartridgeManifest {
  slug: string
  name: string
  description: string
  version: string
  type: 'feature' | 'integration'
  category?: string
  icon?: string
  docsUrl?: string
  dependencies: string[]
  configSchema: Record<string, unknown>
  defaultConfig: Record<string, unknown>
  requiredEnv: string[]
}

interface CartridgeInfo {
  manifest: CartridgeManifest
  status: 'disabled' | 'configured' | 'active'
  config: Record<string, unknown>
  activatedAt?: string
  activatedBy?: string
  isRegistered: boolean
  missingEnv: string[]
  missingDependencies: string[]
}

interface CartridgeHealthResult {
  slug: string
  healthy: boolean
  message?: string
  details?: Record<string, unknown>
}

interface ConfigValidationError {
  field: string
  message: string
  keyword?: string
  params?: Record<string, unknown>
}

interface ConfigUpdateError {
  error: string
  validationErrors?: ConfigValidationError[]
}

// ===========================================
// API Functions
// ===========================================

async function fetchCartridges(): Promise<{ data: CartridgeInfo[]; count: number }> {
  const res = await fetch('/api/cartridges', { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch cartridges')
  return res.json()
}

async function activateCartridge(slug: string): Promise<{ message: string; cartridge: CartridgeInfo }> {
  const res = await fetch(`/api/cartridges/${slug}/activate`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!res.ok) {
    const err = await res.json()
    throw new Error(err.error || 'Failed to activate cartridge')
  }
  return res.json()
}

async function deactivateCartridge(slug: string): Promise<{ message: string; cartridge: CartridgeInfo }> {
  const res = await fetch(`/api/cartridges/${slug}/deactivate`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!res.ok) {
    const err = await res.json()
    throw new Error(err.error || 'Failed to deactivate cartridge')
  }
  return res.json()
}

class ConfigValidationException extends Error {
  validationErrors: ConfigValidationError[]

  constructor(message: string, validationErrors: ConfigValidationError[]) {
    super(message)
    this.name = 'ConfigValidationException'
    this.validationErrors = validationErrors
  }
}

async function updateCartridgeConfig(
  slug: string,
  config: Record<string, unknown>
): Promise<{ message: string; cartridge: CartridgeInfo }> {
  const res = await fetch(`/api/cartridges/${slug}/config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ config }),
  })
  if (!res.ok) {
    const err: ConfigUpdateError = await res.json()
    if (err.validationErrors && err.validationErrors.length > 0) {
      throw new ConfigValidationException(err.error || 'Validation failed', err.validationErrors)
    }
    throw new Error(err.error || 'Failed to update configuration')
  }
  return res.json()
}

async function checkCartridgeHealth(slug: string): Promise<CartridgeHealthResult> {
  const res = await fetch(`/api/cartridges/${slug}/health`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to check health')
  return res.json()
}

// ===========================================
// Helper Components
// ===========================================

function getCartridgeIcon(category?: string, icon?: string) {
  // Map category/icon to Heroicon
  switch (category) {
    case 'erp':
      return CloudIcon
    case 'engineering':
      return CubeIcon
    case 'sales':
      return ShoppingCartIcon
    default:
      return PuzzlePieceIcon
  }
}

function StatusBadge({ status }: { status: CartridgeInfo['status'] }) {
  const { t } = useTranslation()

  const statusStyles = {
    disabled: 'bg-gray-100 text-gray-700',
    configured: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
  }

  const statusLabels = {
    disabled: t('pages.cartridges.status.disabled'),
    configured: t('pages.cartridges.status.configured'),
    active: t('pages.cartridges.status.active'),
  }

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusStyles[status]}`}>
      {status === 'active' && <CheckCircleSolidIcon className="mr-1 h-3 w-3" />}
      {statusLabels[status]}
    </span>
  )
}

function TypeBadge({ type }: { type: 'feature' | 'integration' }) {
  const { t } = useTranslation()

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        type === 'feature' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
      }`}
    >
      {type === 'feature' ? t('pages.cartridges.type.feature') : t('pages.cartridges.type.integration')}
    </span>
  )
}

// ===========================================
// Cartridge Card Component
// ===========================================

function CartridgeCard({
  cartridge,
  onSelect,
}: {
  cartridge: CartridgeInfo
  onSelect: (cartridge: CartridgeInfo) => void
}) {
  const { t } = useTranslation()
  const Icon = getCartridgeIcon(cartridge.manifest.category, cartridge.manifest.icon)
  const hasMissingRequirements = cartridge.missingEnv.length > 0 || cartridge.missingDependencies.length > 0

  return (
    <button
      onClick={() => onSelect(cartridge)}
      className="w-full text-left bg-white rounded-lg border border-gray-200 p-4 hover:border-brand-300 hover:shadow-md transition-all group"
    >
      <div className="flex items-start gap-4">
        <div
          className={`flex-shrink-0 p-3 rounded-lg ${
            cartridge.status === 'active' ? 'bg-brand-100' : 'bg-gray-100'
          }`}
        >
          <Icon className={`h-6 w-6 ${cartridge.status === 'active' ? 'text-brand-600' : 'text-gray-500'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-900">{cartridge.manifest.name}</h3>
            <StatusBadge status={cartridge.status} />
            <TypeBadge type={cartridge.manifest.type} />
          </div>
          <p className="mt-1 text-sm text-gray-500 line-clamp-2">{cartridge.manifest.description}</p>
          <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
            <span>v{cartridge.manifest.version}</span>
            {cartridge.manifest.category && (
              <>
                <span>•</span>
                <span className="capitalize">{cartridge.manifest.category}</span>
              </>
            )}
          </div>
          {hasMissingRequirements && (
            <div className="mt-2 flex items-center gap-1 text-xs text-amber-600">
              <ExclamationTriangleIcon className="h-4 w-4" />
              {t('pages.cartridges.missingRequirements')}
            </div>
          )}
        </div>
        <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-brand-500 flex-shrink-0" />
      </div>
    </button>
  )
}

// ===========================================
// Cartridge Detail Modal Component
// ===========================================

function CartridgeDetailModal({
  cartridge,
  onClose,
  onActivate,
  onDeactivate,
  onConfigSave,
  onHealthCheck,
  isActivating,
  isDeactivating,
  isSavingConfig,
  validationErrors,
  configError,
}: {
  cartridge: CartridgeInfo
  onClose: () => void
  onActivate: () => void
  onDeactivate: () => void
  onConfigSave: (config: Record<string, unknown>) => void
  onHealthCheck: () => void
  isActivating: boolean
  isDeactivating: boolean
  isSavingConfig: boolean
  validationErrors?: ConfigValidationError[]
  configError?: string | null
}) {
  const { t } = useTranslation()
  const [config, setConfig] = useState<Record<string, unknown>>(cartridge.config)
  const [healthResult, setHealthResult] = useState<CartridgeHealthResult | null>(null)
  const [isCheckingHealth, setIsCheckingHealth] = useState(false)
  const queryClient = useQueryClient()

  // Build a map of field -> error for quick lookup
  const fieldErrors = (validationErrors || []).reduce(
    (acc, err) => {
      if (err.field) {
        acc[err.field] = err.message
      }
      return acc
    },
    {} as Record<string, string>
  )

  const Icon = getCartridgeIcon(cartridge.manifest.category, cartridge.manifest.icon)
  const hasMissingRequirements = cartridge.missingEnv.length > 0 || cartridge.missingDependencies.length > 0
  const canActivate = !hasMissingRequirements && cartridge.status !== 'active'

  const handleHealthCheck = async () => {
    setIsCheckingHealth(true)
    try {
      const result = await checkCartridgeHealth(cartridge.manifest.slug)
      setHealthResult(result)
    } catch (error) {
      setHealthResult({
        slug: cartridge.manifest.slug,
        healthy: false,
        message: error instanceof Error ? error.message : 'Health check failed',
      })
    } finally {
      setIsCheckingHealth(false)
    }
  }

  const handleConfigChange = (key: string, value: unknown) => {
    setConfig((prev) => ({ ...prev, [key]: value }))
  }

  const handleSaveConfig = () => {
    onConfigSave(config)
  }

  // Render config fields based on configSchema
  const renderConfigFields = () => {
    const schema = cartridge.manifest.configSchema as {
      type?: string
      properties?: Record<string, { type?: string; description?: string; default?: unknown; enum?: string[] }>
    }

    if (!schema.properties || Object.keys(schema.properties).length === 0) {
      return (
        <p className="text-sm text-gray-500 italic">{t('pages.cartridges.noConfigOptions')}</p>
      )
    }

    return Object.entries(schema.properties).map(([key, fieldSchema]) => {
      const value = config[key] ?? fieldSchema.default ?? ''
      const error = fieldErrors[key]
      const errorClasses = error
        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300 focus:border-brand-500 focus:ring-brand-500'

      if (fieldSchema.type === 'boolean') {
        return (
          <div key={key} className="flex items-center justify-between py-2">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700">{key}</label>
              {fieldSchema.description && (
                <p className="text-xs text-gray-500">{fieldSchema.description}</p>
              )}
              {error && (
                <p className="text-xs text-red-600 mt-1">{error}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => handleConfigChange(key, !value)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                value ? 'bg-brand-600' : 'bg-gray-200'
              } ${error ? 'ring-2 ring-red-500' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  value ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        )
      }

      if (fieldSchema.type === 'number' || fieldSchema.type === 'integer') {
        return (
          <div key={key} className="py-2">
            <label className="block text-sm font-medium text-gray-700">{key}</label>
            {fieldSchema.description && (
              <p className="text-xs text-gray-500 mb-1">{fieldSchema.description}</p>
            )}
            <input
              type="number"
              value={value as number}
              onChange={(e) => handleConfigChange(key, parseInt(e.target.value, 10))}
              className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errorClasses}`}
            />
            {error && (
              <p className="text-xs text-red-600 mt-1">{error}</p>
            )}
          </div>
        )
      }

      // If enum is defined, render as select
      if (fieldSchema.enum && fieldSchema.enum.length > 0) {
        return (
          <div key={key} className="py-2">
            <label className="block text-sm font-medium text-gray-700">{key}</label>
            {fieldSchema.description && (
              <p className="text-xs text-gray-500 mb-1">{fieldSchema.description}</p>
            )}
            <select
              value={value as string}
              onChange={(e) => handleConfigChange(key, e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errorClasses}`}
            >
              {fieldSchema.enum.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {error && (
              <p className="text-xs text-red-600 mt-1">{error}</p>
            )}
          </div>
        )
      }

      // Default to text input
      return (
        <div key={key} className="py-2">
          <label className="block text-sm font-medium text-gray-700">{key}</label>
          {fieldSchema.description && (
            <p className="text-xs text-gray-500 mb-1">{fieldSchema.description}</p>
          )}
          <input
            type="text"
            value={value as string}
            onChange={(e) => handleConfigChange(key, e.target.value)}
            className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errorClasses}`}
          />
          {error && (
            <p className="text-xs text-red-600 mt-1">{error}</p>
          )}
        </div>
      )
    })
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div
                  className={`flex-shrink-0 p-3 rounded-lg ${
                    cartridge.status === 'active' ? 'bg-brand-100' : 'bg-gray-100'
                  }`}
                >
                  <Icon
                    className={`h-6 w-6 ${cartridge.status === 'active' ? 'text-brand-600' : 'text-gray-500'}`}
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{cartridge.manifest.name}</h3>
                  <div className="mt-1 flex items-center gap-2">
                    <StatusBadge status={cartridge.status} />
                    <TypeBadge type={cartridge.manifest.type} />
                    <span className="text-xs text-gray-400">v{cartridge.manifest.version}</span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="rounded-md text-gray-400 hover:text-gray-500"
                onClick={onClose}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {/* Description */}
            <p className="text-sm text-gray-600 mb-4">{cartridge.manifest.description}</p>

            {/* Docs link */}
            {cartridge.manifest.docsUrl && (
              <a
                href={cartridge.manifest.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 mb-4"
              >
                <DocumentTextIcon className="h-4 w-4" />
                {t('pages.cartridges.viewDocs')}
              </a>
            )}

            {/* Missing Requirements Warning */}
            {hasMissingRequirements && (
              <div className="mb-4 rounded-md bg-amber-50 p-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-amber-800">
                      {t('pages.cartridges.cannotActivate')}
                    </h4>
                    {cartridge.missingEnv.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-amber-700">{t('pages.cartridges.missingEnvVars')}:</p>
                        <ul className="mt-1 list-disc list-inside text-sm text-amber-600">
                          {cartridge.missingEnv.map((env) => (
                            <li key={env}>
                              <code className="bg-amber-100 px-1 rounded">{env}</code>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {cartridge.missingDependencies.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-amber-700">{t('pages.cartridges.missingDeps')}:</p>
                        <ul className="mt-1 list-disc list-inside text-sm text-amber-600">
                          {cartridge.missingDependencies.map((dep) => (
                            <li key={dep}>{dep}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Dependencies */}
            {cartridge.manifest.dependencies.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-1">
                  <LinkIcon className="h-4 w-4" />
                  {t('pages.cartridges.dependencies')}
                </h4>
                <ul className="flex flex-wrap gap-2">
                  {cartridge.manifest.dependencies.map((dep) => (
                    <li
                      key={dep}
                      className={`text-xs px-2 py-1 rounded-full ${
                        cartridge.missingDependencies.includes(dep)
                          ? 'bg-red-100 text-red-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {dep}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Configuration */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-1">
                <Cog6ToothIcon className="h-4 w-4" />
                {t('pages.cartridges.configuration')}
              </h4>

              {/* Config error banner */}
              {configError && (
                <div className="mb-3 rounded-md bg-red-50 p-3">
                  <div className="flex">
                    <XCircleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{configError}</p>
                      {validationErrors && validationErrors.length > 0 && (
                        <p className="text-xs text-red-600 mt-1">
                          {t('pages.cartridges.fixValidationErrors')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">{renderConfigFields()}</div>
            </div>

            {/* Health Status */}
            {cartridge.status === 'active' && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-1">
                  <HeartIcon className="h-4 w-4" />
                  {t('pages.cartridges.health')}
                </h4>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleHealthCheck}
                    disabled={isCheckingHealth}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ArrowPathIcon className={`h-4 w-4 ${isCheckingHealth ? 'animate-spin' : ''}`} />
                    {t('pages.cartridges.checkHealth')}
                  </button>
                  {healthResult && (
                    <div
                      className={`flex items-center gap-1 text-sm ${
                        healthResult.healthy ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {healthResult.healthy ? (
                        <CheckCircleIcon className="h-5 w-5" />
                      ) : (
                        <XCircleIcon className="h-5 w-5" />
                      )}
                      {healthResult.message || (healthResult.healthy ? 'Healthy' : 'Unhealthy')}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Activation Info */}
            {cartridge.activatedAt && (
              <div className="text-xs text-gray-500">
                {t('pages.cartridges.activatedAt')}:{' '}
                {new Date(cartridge.activatedAt).toLocaleString()}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 flex justify-between">
            <div>
              {cartridge.status === 'active' ? (
                <button
                  type="button"
                  onClick={onDeactivate}
                  disabled={isDeactivating}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50"
                >
                  <StopIcon className="h-4 w-4" />
                  {isDeactivating ? t('pages.cartridges.deactivating') : t('pages.cartridges.deactivate')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onActivate}
                  disabled={!canActivate || isActivating}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlayIcon className="h-4 w-4" />
                  {isActivating ? t('pages.cartridges.activating') : t('pages.cartridges.activate')}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t('common.close')}
              </button>
              <button
                type="button"
                onClick={handleSaveConfig}
                disabled={isSavingConfig}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50"
              >
                {isSavingConfig ? t('common.saving') : t('pages.cartridges.saveConfig')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ===========================================
// Main Page Component
// ===========================================

export function CartridgesPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [selectedCartridge, setSelectedCartridge] = useState<CartridgeInfo | null>(null)
  const [configValidationErrors, setConfigValidationErrors] = useState<ConfigValidationError[]>([])
  const [configError, setConfigError] = useState<string | null>(null)

  // Clear errors when closing modal
  const handleCloseModal = () => {
    setSelectedCartridge(null)
    setConfigValidationErrors([])
    setConfigError(null)
  }

  // Queries
  const { data, isLoading } = useQuery({
    queryKey: ['cartridges'],
    queryFn: fetchCartridges,
  })

  // Mutations
  const activateMutation = useMutation({
    mutationFn: activateCartridge,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cartridges'] })
      setSelectedCartridge(null)
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: deactivateCartridge,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cartridges'] })
      setSelectedCartridge(null)
    },
  })

  const configMutation = useMutation({
    mutationFn: ({ slug, config }: { slug: string; config: Record<string, unknown> }) =>
      updateCartridgeConfig(slug, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cartridges'] })
      // Clear errors on success
      setConfigValidationErrors([])
      setConfigError(null)
    },
    onError: (error) => {
      if (error instanceof ConfigValidationException) {
        setConfigValidationErrors(error.validationErrors)
        setConfigError(error.message)
      } else {
        setConfigValidationErrors([])
        setConfigError(error.message)
      }
    },
  })

  // Group cartridges by category
  const groupedCartridges = data?.data.reduce(
    (acc, cartridge) => {
      const category = cartridge.manifest.category || 'other'
      if (!acc[category]) acc[category] = []
      acc[category].push(cartridge)
      return acc
    },
    {} as Record<string, CartridgeInfo[]>
  )

  const activeCount = data?.data.filter((c) => c.status === 'active').length || 0
  const totalCount = data?.count || 0

  if (isLoading) return <div className="p-8">{t('common.loading')}</div>

  return (
    <>
      {/* Secondary column - Stats and Info */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('pages.cartridges.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">{t('pages.cartridges.description')}</p>
          </div>

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('pages.cartridges.stats')}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.cartridges.totalCartridges')}</dt>
                <dd className="font-medium text-gray-900">{totalCount}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.cartridges.activeCartridges')}</dt>
                <dd className="font-medium text-green-600">{activeCount}</dd>
              </div>
            </dl>
          </div>

          {/* Info Box */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-2">{t('pages.cartridges.aboutTitle')}</h3>
            <p className="text-sm text-gray-500">{t('pages.cartridges.aboutDescription')}</p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('pages.cartridges.title')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('pages.cartridges.description')}</p>
          </div>

          {/* Cartridge Grid */}
          {groupedCartridges && Object.keys(groupedCartridges).length > 0 ? (
            <div className="space-y-8">
              {Object.entries(groupedCartridges).map(([category, cartridges]) => (
                <div key={category}>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                    {category}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {cartridges.map((cartridge) => (
                      <CartridgeCard
                        key={cartridge.manifest.slug}
                        cartridge={cartridge}
                        onSelect={setSelectedCartridge}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <PuzzlePieceIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-semibold text-gray-900">
                {t('pages.cartridges.noCartridges')}
              </h3>
              <p className="mt-1 text-sm text-gray-500">{t('pages.cartridges.noCartridgesHint')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedCartridge && (
        <CartridgeDetailModal
          cartridge={selectedCartridge}
          onClose={handleCloseModal}
          onActivate={() => activateMutation.mutate(selectedCartridge.manifest.slug)}
          onDeactivate={() => {
            if (confirm(t('pages.cartridges.confirmDeactivate'))) {
              deactivateMutation.mutate(selectedCartridge.manifest.slug)
            }
          }}
          onConfigSave={(config) =>
            configMutation.mutate({ slug: selectedCartridge.manifest.slug, config })
          }
          onHealthCheck={() => {}}
          isActivating={activateMutation.isPending}
          isDeactivating={deactivateMutation.isPending}
          isSavingConfig={configMutation.isPending}
          validationErrors={configValidationErrors}
          configError={configError}
        />
      )}
    </>
  )
}
